// Operator observability for the Signal Orchestrator (DEV-16226).
// Cross-package contract: response schemas live here; the api projects over
// the existing Event table + Redis worker heartbeats — no new persistence.
import { z } from 'zod';
import { WorkerHeartbeatSchema } from './ops.js';
/** Per-decision row surfaced in the admin "recent decisions" list. */
export const OrchestratorRecentDecisionSchema = z.object({
    runId: z.string(),
    /** The workflow the deduplicated run belonged to (display name + id). */
    workflowId: z.string().nullable(),
    workflowName: z.string().nullable(),
    signalId: z.string().nullable(),
    cancelledByRunId: z.string().nullable(),
    signalKey: z.string(),
    mode: z.enum(['A', 'B']),
    /**
     * Which engine made the call — L1 deterministic, L2 semantic, L3 LLM.
     * Backfill-safe: events emitted before the layer-aware payload landed
     * default to `L1` (the only layer that existed at the time).
     */
    layer: z.enum(['L1', 'L2', 'L3']),
    /**
     * Layer-specific confidence (cosine similarity for L2, LLM confidence
     * for L3). Null for L1 + every Mode B (always L1).
     */
    confidence: z.number().min(0).max(1).nullable(),
    enforced: z.boolean(),
    reason: z.string(),
    at: z.string().datetime(),
});
/**
 * Aggregated operator view. Always computed over `windowHours` ending now —
 * caller passes `?windowHours=24` (default).
 */
export const OrchestratorStatsResponseSchema = z.object({
    windowHours: z.number().int().positive(),
    /** Run totals + dedup rate over the window. */
    runs: z.object({
        total: z.number().int().nonnegative(),
        /** Runs the orchestrator decided on (any mode, enforce or dry-run). */
        deduplicated: z.number().int().nonnegative(),
        /** deduplicated / total, 0 when total = 0. */
        dedupRate: z.number(),
    }),
    /** Signal lifecycle over the window + the current open count. */
    signals: z.object({
        opened: z.number().int().nonnegative(),
        resolved: z.number().int().nonnegative(),
        openCount: z.number().int().nonnegative(),
    }),
    /** Decision counts split by match mode (A = open signal, B = in-flight run). */
    decisionsByMode: z.object({
        A: z.number().int().nonnegative(),
        B: z.number().int().nonnegative(),
    }),
    /** Decision counts split by engine (which layer made the call). */
    decisionsByLayer: z.object({
        L1: z.number().int().nonnegative(),
        L2: z.number().int().nonnegative(),
        L3: z.number().int().nonnegative(),
    }),
    /** Decision counts split by enforcement (audit for dry-run rollout). */
    decisionsByEnforcement: z.object({
        enforced: z.number().int().nonnegative(),
        dryRun: z.number().int().nonnegative(),
    }),
    /** Most recent decisions (newest first), capped server-side. */
    recentDecisions: z.array(OrchestratorRecentDecisionSchema),
    /**
     * Live orchestrator pods. Empty array = no leader is reporting; admins
     * should treat that as "orchestrator is down" and investigate.
     */
    workers: z.array(WorkerHeartbeatSchema),
});
//# sourceMappingURL=orchestrator.js.map